import React, { useState, useEffect } from "react";
import {
  X,
  Calendar,
  Gauge,
  MapPin,
  User,
  Wrench,
  FileText,
} from "lucide-react";
import {
  useCreateMaintenanceRecord,
  useUpdateMaintenanceRecord,
} from "@/hooks/useVehicleMaintenance";
import { useTranslation } from "react-i18next";
import type {
  Vehicle,
  VehicleMaintenance,
  MaintenanceType,
  CreateVehicleMaintenanceRequest,
} from "@/types";

interface VehicleMaintenanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  vehicle: Vehicle;
  maintenance?: VehicleMaintenance;
}

export function VehicleMaintenanceModal({
  isOpen,
  onClose,
  vehicle,
  maintenance,
}: VehicleMaintenanceModalProps) {
  const { t } = useTranslation(["pages", "common"]);
  const [formData, setFormData] = useState({
    performedAt: "",
    mileage: "",
    shopName: "",
    maintainers: "",
    type: "scheduled" as MaintenanceType,
    workType: "",
    description: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const createMaintenance = useCreateMaintenanceRecord();
  const updateMaintenance = useUpdateMaintenanceRecord();

  const isEditing = !!maintenance;
  const isLoading = createMaintenance.isPending || updateMaintenance.isPending;

  useEffect(() => {
    if (isOpen) {
      if (maintenance) {
        // Editing existing record - preserve the original time
        const originalDate = new Date(maintenance.performedAt);
        setFormData({
          performedAt: originalDate.toISOString().split("T")[0],
          mileage: maintenance.mileage?.toString() || "",
          shopName: maintenance.shopName || "",
          maintainers: maintenance.maintainers || "",
          type: maintenance.type,
          workType: maintenance.workType,
          description: maintenance.description || "",
        });
      } else {
        // Creating new record
        setFormData({
          performedAt: new Date().toISOString().split("T")[0],
          mileage: "",
          shopName: "",
          maintainers: "",
          type: "scheduled",
          workType: "",
          description: "",
        });
      }
      setErrors({});
    }
  }, [isOpen, maintenance]);

  // Store original maintenance date to preserve time when editing
  const originalMaintenanceDate = maintenance
    ? new Date(maintenance.performedAt)
    : null;

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.performedAt) {
      newErrors.performedAt = t("pages:vehicles.maintenance.validation.datePerformedRequired");
    }

    if (!formData.workType.trim()) {
      newErrors.workType = t("pages:vehicles.maintenance.validation.workTypeRequired");
    }

    if (formData.mileage && isNaN(Number(formData.mileage))) {
      newErrors.mileage = t("pages:vehicles.maintenance.validation.mileageInvalid");
    }

    if (formData.mileage && Number(formData.mileage) < 0) {
      newErrors.mileage = t("pages:vehicles.maintenance.validation.mileageNegative");
    }

    setErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const isFormValid = validateForm();

    if (!isFormValid) {
      return;
    }

    try {
      // When editing, preserve the original time. When creating, use date as-is (midnight)
      let performedAtDate: Date;
      if (isEditing && originalMaintenanceDate) {
        // For editing: combine the new date with the original time
        // (both operate on UTC calendar fields to match the seeded input)
        const newDate = new Date(formData.performedAt);
        performedAtDate = new Date(
          Date.UTC(
            newDate.getUTCFullYear(),
            newDate.getUTCMonth(),
            newDate.getUTCDate(),
            originalMaintenanceDate.getUTCHours(),
            originalMaintenanceDate.getUTCMinutes(),
            originalMaintenanceDate.getUTCSeconds(),
            originalMaintenanceDate.getUTCMilliseconds(),
          ),
        );
      } else {
        // For creating: use the date as-is (midnight)
        performedAtDate = new Date(formData.performedAt);
      }

      const maintenanceData: CreateVehicleMaintenanceRequest = {
        vehicleId: vehicle.id,
        performedAt: performedAtDate.toISOString(),
        type: formData.type,
        workType: formData.workType.trim(),
        mileage: formData.mileage ? Number(formData.mileage) : undefined,
        shopName: formData.shopName.trim() || undefined,
        maintainers: formData.maintainers.trim() || undefined,
        description: formData.description.trim() || undefined,
      };

      if (isEditing) {
        await updateMaintenance.mutateAsync({
          id: maintenance.id,
          data: maintenanceData,
        });
      } else {
        await createMaintenance.mutateAsync(maintenanceData);
      }

      onClose();
    } catch (error) {
      console.error("Failed to save maintenance record:", error);
      // TODO: Show error toast
    }
  };

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  if (!isOpen) return null;

  const vehicleDisplayName =
    vehicle.name ||
    `${vehicle.year} ${vehicle.maker} ${vehicle.model}`;

  return (
 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
 <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between p-6 border-b border-secondary-200">
          <div>
            <h2 className="text-xl font-semibold text-secondary-900">
              {isEditing ? t("pages:vehicles.maintenance.modalEditTitle") : t("pages:vehicles.maintenance.modalCreateTitle")}
            </h2>
            <p className="text-sm text-secondary-600 mt-1">
              {t("pages:vehicles.maintenance.modalSubtitle", {
                vehicle: vehicleDisplayName,
              })}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-secondary-400 hover:text-secondary-600 rounded-lg transition-colors"
            aria-label={t("pages:vehicles.maintenance.closeAria")}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

 <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Date and Work Type */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="performedAt"
                className="block text-sm font-medium text-secondary-700 mb-1"
              >
                <Calendar className="inline h-4 w-4 mr-1" />
                {t("pages:vehicles.maintenance.fields.datePerformed")} *
              </label>
              <input
                id="performedAt"
                type="date"
                value={formData.performedAt}
                onChange={(e) =>
                  handleInputChange("performedAt", e.target.value)
                }
                className={`input-field ${errors.performedAt ? "border-red-300" : ""}`}
                required
              />
              {errors.performedAt && (
                <p className="text-red-600 text-sm mt-1">
                  {errors.performedAt}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="workType"
                className="block text-sm font-medium text-secondary-700 mb-1"
              >
                <Wrench className="inline h-4 w-4 mr-1" />
                {t("pages:vehicles.maintenance.fields.workType")} *
              </label>
              <input
                id="workType"
                type="text"
                value={formData.workType}
                onChange={(e) => handleInputChange("workType", e.target.value)}
                placeholder={t("pages:vehicles.maintenance.placeholders.workType")}
                className={`input-field ${errors.workType ? "border-red-300" : ""}`}
                required
              />
              {errors.workType && (
                <p className="text-red-600 text-sm mt-1">{errors.workType}</p>
              )}
            </div>
          </div>

          {/* Type and Mileage */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="type"
                className="block text-sm font-medium text-secondary-700 mb-1"
              >
                {t("pages:vehicles.maintenance.fields.maintenanceType")}
              </label>
              <select
                id="type"
                value={formData.type}
                onChange={(e) => handleInputChange("type", e.target.value)}
                className="input-field"
                required
              >
                <option value="scheduled">{t("pages:vehicles.maintenanceTypes.scheduled")}</option>
                <option value="unexpected">{t("pages:vehicles.maintenanceTypes.unexpected")}</option>
                <option value="preventative">{t("pages:vehicles.maintenanceTypes.preventative")}</option>
                <option value="other">{t("pages:vehicles.maintenanceTypes.other")}</option>
              </select>
            </div>

            <div>
              <label
                htmlFor="mileage"
                className="block text-sm font-medium text-secondary-700 mb-1"
              >
                <Gauge className="inline h-4 w-4 mr-1" />
                {t("pages:vehicles.maintenance.fields.mileage")}
              </label>
              <input
                id="mileage"
                type="number"
                value={formData.mileage}
                onChange={(e) => handleInputChange("mileage", e.target.value)}
                placeholder={t("pages:vehicles.maintenance.placeholders.mileage")}
                min="0"
                className={`input-field ${errors.mileage ? "border-red-300" : ""}`}
              />
              {errors.mileage && (
                <p className="text-red-600 text-sm mt-1">{errors.mileage}</p>
              )}
            </div>
          </div>

          {/* Shop and Maintainer */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="shopName"
                className="block text-sm font-medium text-secondary-700 mb-1"
              >
                <MapPin className="inline h-4 w-4 mr-1" />
                {t("pages:vehicles.maintenance.fields.shopName")}
              </label>
              <input
                id="shopName"
                type="text"
                value={formData.shopName}
                onChange={(e) => handleInputChange("shopName", e.target.value)}
                placeholder={t("pages:vehicles.maintenance.placeholders.shopName")}
                className="input-field"
              />
            </div>

            <div>
              <label
                htmlFor="maintainers"
                className="block text-sm font-medium text-secondary-700 mb-1"
              >
                <User className="inline h-4 w-4 mr-1" />
                {t("pages:vehicles.maintenance.fields.maintainers")}
              </label>
              <input
                id="maintainers"
                type="text"
                value={formData.maintainers}
                onChange={(e) =>
                  handleInputChange("maintainers", e.target.value)
                }
                placeholder={t("pages:vehicles.maintenance.placeholders.maintainers")}
                className="input-field"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label
              htmlFor="description"
              className="block text-sm font-medium text-secondary-700 mb-1"
            >
              <FileText className="inline h-4 w-4 mr-1" />
              {t("common:description")}
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              placeholder={t("pages:vehicles.maintenance.placeholders.description")}
              rows={4}
              className="input-field resize-none"
            />
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-secondary-200">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary px-4 py-2"
              disabled={isLoading}
            >
              {t("common:cancel")}
            </button>
            <button
              type="submit"
              className="btn-primary px-4 py-2"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {isEditing ? t("common:updating") : t("common:creating")}
                </>
              ) : isEditing ? (
                t("pages:vehicles.maintenance.updateRecord")
              ) : (
                t("pages:vehicles.maintenance.createRecord")
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
